import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/components/ui/button";
import {
  applyConsentUpdate,
  getInitialConsentStatus,
  isAnalyticsEnabled,
  type ConsentStatus,
} from "@/shared/lib/analytics";
import { LANDING_PRIVACY_POLICY_URL } from "@/shared/config/marketingUrls";

import "./cookie-consent-banner.css";

export const CookieConsentBanner = memo(function CookieConsentBanner() {
  const { t } = useTranslation("layout");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isAnalyticsEnabled()) {
      return;
    }

    if (!getInitialConsentStatus()) {
      setVisible(true);
    }
  }, []);

  const handleChoice = useCallback((status: ConsentStatus) => {
    applyConsentUpdate(status);
    setVisible(false);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieConsent.ariaLabel")}
    >
      <div className="cookie-consent-banner__inner">
        <p className="cookie-consent-banner__text">
          {t("cookieConsent.message")}{" "}
          <a
            className="cookie-consent-banner__link"
            href={LANDING_PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("cookieConsent.privacyLink")}
          </a>
        </p>
        <div className="cookie-consent-banner__actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleChoice("denied")}
          >
            {t("cookieConsent.reject")}
          </Button>
          <Button type="button" size="sm" onClick={() => handleChoice("granted")}>
            {t("cookieConsent.accept")}
          </Button>
        </div>
      </div>
    </div>
  );
});
